import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { invokeFunction } from '../lib/api';

interface SimilarApp {
  similar_project_id: string;
  name: string;
  description: string;
  slug: string;
  public_id: string;
  logo_emoji: string | null;
  rank: number;
  similarity: number;
}

interface SimilarMiniAppsProps {
  projectId: string;
}

export function SimilarMiniApps({ projectId }: SimilarMiniAppsProps) {
  const [apps, setApps] = useState<SimilarApp[]>([]);

  useEffect(() => {
    let cancelled = false;
    setApps([]);

    invokeFunction<{ results: SimilarApp[] }>('get-similar-miniapps', {
      projectId,
    }).then(({ data }) => {
      if (cancelled) return;
      setApps(data?.results?.slice(0, 3) || []);
    }).catch(() => {
      // silently ignore
    });

    return () => { cancelled = true; };
  }, [projectId]);

  // Render nothing until data arrives — the section pops in asynchronously
  if (apps.length === 0) return null;

  return (
    <section className="border-t border-theme-border-muted bg-theme-bg">
      <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8">
        <h3 className="text-sm font-semibold text-theme-text-secondary mb-4">
          More mini-apps you might like
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {apps.map((app) => (
            <Link
              key={app.similar_project_id}
              to={`/p/${app.public_id}/${app.slug || ''}`}
              className="card p-4 hover:border-accent-500/50 transition-all duration-200 group"
            >
              <h4 className="font-semibold text-sm text-theme-text mb-1 group-hover:text-accent-500 transition-colors line-clamp-1">
                {app.logo_emoji && <span className="mr-1.5">{app.logo_emoji}</span>}
                {app.name}
              </h4>
              {app.description && (
                <p className="text-theme-text-secondary text-xs line-clamp-2">
                  {app.description}
                </p>
              )}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
